import { useState, useEffect } from 'react';
import { useGame } from '../GameContext';
import { SoldierType, UnitStats } from '../types';
import './UnitManager.css';

const UNIT_TYPES: SoldierType[] = ['King', 'Knight', 'Archer', 'Crossbow', 'Healer', 'Siege Equipment'];

const UNIT_ICONS: Record<SoldierType, string> = {
  'King': '👑',
  'Knight': '⚔️',
  'Archer': '🏹',
  'Crossbow': '🎯',
  'Healer': '✚',
  'Siege Equipment': '🏛️'
};

const UnitManager: React.FC = () => {
  const { gameState, updateUnitStats } = useGame();
  const [editedStats, setEditedStats] = useState<Record<SoldierType, UnitStats>>(gameState.unitStats);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Update local state when gameState changes (e.g., after save)
  useEffect(() => {
    setEditedStats(gameState.unitStats);
    setHasChanges(false);
  }, [gameState.unitStats]);

  const handleUpdateStat = (unitType: SoldierType, field: 'hp' | 'damage' | 'cost' | 'imageUrl', value: string) => {
    if (field === 'imageUrl') {
      setEditedStats(prev => ({
        ...prev,
        [unitType]: {
          ...prev[unitType],
          [field]: value
        }
      }));
    } else {
      const numValue = parseInt(value) || 0;
      setEditedStats(prev => ({
        ...prev,
        [unitType]: {
          ...prev[unitType],
          [field]: numValue
        }
      }));
    }
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Update each unit type that has changes
      for (const unitType of UNIT_TYPES) {
        const original = gameState.unitStats[unitType];
        const edited = editedStats[unitType];

        if (original.hp !== edited.hp || original.damage !== edited.damage || original.cost !== edited.cost || original.imageUrl !== edited.imageUrl) {
          await updateUnitStats(unitType, {
            hp: edited.hp,
            damage: edited.damage,
            cost: edited.cost,
            imageUrl: edited.imageUrl
          });
        }
      }
      setHasChanges(false);
    } catch (err) {
      console.error('Failed to save unit stats:', err);
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setEditedStats(gameState.unitStats);
    setHasChanges(false);
  };

  return (
    <div className="unit-manager-container">
      <div className="unit-manager-header">
        <div>
          <h1>Unit Manager</h1>
          <p className="description">Manage stats for each unit type. Changes to costs will affect army compositions.</p>
        </div>
        <div className="action-buttons">
          {hasChanges && (
            <>
              <button
                className="reset-button"
                onClick={handleReset}
                disabled={isSaving}
              >
                Reset
              </button>
              <button
                className="save-button"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="units-grid">
        {UNIT_TYPES.map(unitType => {
          const stats = editedStats[unitType];

          return (
            <div key={unitType} className="unit-card">
              <div className="unit-header">
                <span className="unit-icon">{UNIT_ICONS[unitType]}</span>
                <h2>{unitType}</h2>
              </div>

              <div className="stat-row">
                <label>Image URL:</label>
                <input
                  type="text"
                  value={stats.imageUrl || ''}
                  onChange={(e) => handleUpdateStat(unitType, 'imageUrl', e.target.value)}
                  placeholder="https://example.com/image.png"
                />
              </div>

              {stats.imageUrl && (
                <div className="image-preview">
                  <img
                    src={stats.imageUrl}
                    alt={`${unitType} preview`}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}

              <div className="stat-row">
                <label>HP:</label>
                <input
                  type="number"
                  value={stats.hp}
                  onChange={(e) => handleUpdateStat(unitType, 'hp', e.target.value)}
                  min="1"
                />
              </div>

              <div className="stat-row">
                <label>Damage:</label>
                <input
                  type="number"
                  value={stats.damage}
                  onChange={(e) => handleUpdateStat(unitType, 'damage', e.target.value)}
                  min="0"
                />
              </div>

              <div className="stat-row">
                <label>Cost (Raid Points):</label>
                <input
                  type="number"
                  value={stats.cost}
                  onChange={(e) => handleUpdateStat(unitType, 'cost', e.target.value)}
                  min="0"
                  className={unitType === 'King' ? 'readonly' : ''}
                  disabled={unitType === 'King'}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UnitManager;
